/** Artifact positions — list view (artifact placement within contours/phases). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import { Icon } from "@/components/ui/icon";
import type { ArtifactPosition } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefLink } from "@/components/inspector";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import { RowActions } from "../_shared/ListScaffold";

import {
  DataTable,
  PageHeader,
  SearchBox,
  useSearchFilter,
  type RefineTableCore,
} from "../projects/_shared";

export function ArtifactPositionList() {
  const go = useGo();
  const { editUrl, showUrl, createUrl } = useNavigation();
  const { mutate: deletePosition } = useDelete();
  const [pendingDelete, setPendingDelete] =
    React.useState<ArtifactPosition | null>(null);

  const columns = useMemo<ColumnDef<ArtifactPosition, unknown>[]>(
    () => [
      {
        id: "artifact_id",
        accessorKey: "artifact_id",
        header: "Artifact",
        cell: ({ row }) => (
          <RefLink
            resource="artifacts"
            id={row.original.artifact_id}
            resolve={false}
          />
        ),
      },
      {
        id: "process_id",
        accessorKey: "process_id",
        header: "Contour",
        cell: ({ row }) => (
          <RefLink
            resource="packages"
            id={row.original.process_id}
            resolve={false}
          />
        ),
      },
      {
        id: "phase_id",
        accessorKey: "phase_id",
        header: "Phase / Mode",
        cell: ({ row }) => (
          <span className="text-sm">{row.original.phase_id}</span>
        ),
      },
      {
        id: "is_primary",
        accessorKey: "is_primary",
        header: "Placement",
        cell: ({ row }) =>
          row.original.is_primary === 1 ? (
            <Badge variant="secondary">Primary phase</Badge>
          ) : (
            <Badge variant="outline">Scored mode</Badge>
          ),
      },
      {
        id: "membership_score",
        accessorKey: "membership_score",
        header: "Membership",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.membership_score.toFixed(2)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onShow={() =>
              go({ to: showUrl("artifact-positions", row.original.id) })
            }
            onEdit={() =>
              go({ to: editUrl("artifact-positions", row.original.id) })
            }
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  const table = useTable<ArtifactPosition, HttpError>({
    columns,
    refineCoreProps: {
      resource: "artifact-positions",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "updated_at", order: "desc" }] },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: { field: string; operator: "contains"; value: string }[],
    ) => void;
  };
  const [search, setSearch] = useSearchFilter(core.setFilters);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Artifact Positions"
        description="Where artifacts sit within contours — a single phase (strong) or scored modes (weak)."
        action={
          <Button onClick={() => go({ to: createUrl("artifact-positions") })}>
            <Icon name="create" size={16} className="mr-2" aria-hidden /> New
            Position
          </Button>
        }
      />

      <SearchBox
        value={search}
        onChange={setSearch}
        placeholder="Search positions…"
      />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No artifact positions yet."
        onRowClick={(p) => go({ to: showUrl("artifact-positions", p.id) })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open: boolean) => !open && setPendingDelete(null)}
        title="Delete position?"
        description={
          <>This removes the artifact position. This cannot be undone.</>
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) {
            deletePosition({
              resource: "artifact-positions",
              id: pendingDelete.id,
            });
          }
        }}
      />
    </div>
  );
}

export default ArtifactPositionList;
